import React, { useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { setOverlayVisible } from "../../redux/actions/overlay";
import { useNavigate } from "react-router-dom";
import {
  Fab,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { ReactComponent as AddIcon } from "../../assets/icons/add_black_24dp.svg";
import MoodIcon from "@mui/icons-material/Mood";
import AppsIcon from "@mui/icons-material/Apps";
import EventRepeatIcon from "@mui/icons-material/EventRepeatOutlined";
import ChecklistIcon from "@mui/icons-material/Checklist";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import GroupIcon from "@mui/icons-material/Group";
import CakeIcon from "@mui/icons-material/Cake";
import TimelineIcon from "@mui/icons-material/Timeline";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { ActivitiesContext } from "../../context/activities/ActivitiesContext";
import useCreateTaskAction from "../../hooks/tasks/useCreateTaskAction";
import TaskCreationPreferenceDialog from "../dialogs/TaskCreationPreferenceDialog";
import TaskHelperDialog from "../tasks/taskCreator/TaskHelperDialog";

/**
 * Reusable footer component for Activities, Tasks, and Planner pages.
 *
 * @param {Object} props
 * @param {'tasks' | 'planner' | 'activities' | 'dashboard'} props.page - The current page type
 * @param {Function} [props.onOpenTaskHelper] - Optional external handler for opening the guided creator
 *   (used by pages that already render their own TaskHelperDialog, e.g. TasksPage, DashboardPage)
 */
const AppFooter = ({ page, onOpenTaskHelper: externalOpenTaskHelper }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);

  // Only use ActivitiesContext when on activities page
  const activitiesContext = useContext(ActivitiesContext);
  const openCreateDialog = activitiesContext?.openCreateDialog;

  // Footer's own TaskHelper state (used when no external handler is provided)
  const [footerTaskHelperOpen, setFooterTaskHelperOpen] = useState(false);

  const openGuidedCreator = externalOpenTaskHelper || (() => setFooterTaskHelperOpen(true));

  const {
    triggerCreateTask,
    showPreferenceDialog,
    setShowPreferenceDialog,
    handlePreferenceSelected,
  } = useCreateTaskAction(page, openGuidedCreator);

  const onMindsetClick = () => {
    dispatch(
      setOverlayVisible({
        visible: true,
        overlayType: "mindset",
      }),
    );
  };

  const onMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const onMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const onTasksClick = () => {
    setMenuAnchorEl(null);
    navigate("/tasks");
  };

  const onRecurringTasksClick = () => {
    setMenuAnchorEl(null);
    navigate("/recurring-tasks");
  };

  const onPlannerClick = () => {
    setMenuAnchorEl(null);
    navigate("/planner");
  };

  const onActivitiesClick = () => {
    setMenuAnchorEl(null);
    navigate("/activities");
  };

  const onCompletedTasksClick = () => {
    setMenuAnchorEl(null);
    navigate("/completed-tasks");
  };

  const onDashboardClick = () => {
    setMenuAnchorEl(null);
    navigate("/dashboard");
  };

  const onAddButtonClick = () => {
    if (page === "activities") {
      openCreateDialog?.();
    } else {
      triggerCreateTask();
    }
  };

  const getAddButtonTooltip = () => {
    if (page === "activities") return "Add Activity";
    if (page === "planner") return "Create Task";
    return "Create Task";
  };

  // Build menu items based on current page (hide current page from menu)
  const menuItems = [];

  // Dashboard at the top
  if (page !== "dashboard") {
    menuItems.push(
      <MenuItem key="dashboard" onClick={onDashboardClick}>
        <ListItemIcon>
          <DashboardIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Dashboard</ListItemText>
      </MenuItem>,
    );
  }

  // Tasks second
  if (page !== "tasks") {
    menuItems.push(
      <MenuItem key="tasks" onClick={onTasksClick}>
        <ListItemIcon>
          <ChecklistIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Tasks</ListItemText>
      </MenuItem>,
    );
  }

  // Day Planner second
  if (page !== "planner") {
    menuItems.push(
      <MenuItem key="planner" onClick={onPlannerClick}>
        <ListItemIcon>
          <CalendarMonthIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Day Planner</ListItemText>
      </MenuItem>,
    );
  }

  // Activities third
  if (page !== "activities") {
    menuItems.push(
      <MenuItem key="activities" onClick={onActivitiesClick}>
        <ListItemIcon>
          <SelfImprovementIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Activities</ListItemText>
      </MenuItem>,
    );
  }

  // Recurring Tasks fourth
  menuItems.push(
    <MenuItem key="recurring-tasks" onClick={onRecurringTasksClick}>
      <ListItemIcon>
        <EventRepeatIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>Recurring Tasks</ListItemText>
    </MenuItem>,
  );

  // Completed Tasks fifth
  if (page !== "completed-tasks") {
    menuItems.push(
      <MenuItem key="completed-tasks" onClick={onCompletedTasksClick}>
        <ListItemIcon>
          <EmojiEventsIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Completed Tasks</ListItemText>
      </MenuItem>,
    );
  }

  // Add locked/disabled menu items
  menuItems.push(
    <MenuItem key="people" disabled>
      <ListItemIcon>
        <GroupIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>People</ListItemText>
    </MenuItem>,
    <MenuItem key="treats" disabled>
      <ListItemIcon>
        <CakeIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>Treats</ListItemText>
    </MenuItem>,
    <MenuItem key="trackers" disabled>
      <ListItemIcon>
        <TimelineIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>Trackers</ListItemText>
    </MenuItem>,
  );

  return (
    <div className="footer">
      {/* More menu button */}
      <Tooltip title="More" placement="top">
        <Fab
          color="blue"
          size="medium"
          className="footer__icon-button animate__animated animate__heartBeat animate__delay-2s"
          onClick={onMenuClick}
        >
          <AppsIcon color="white" />
        </Fab>
      </Tooltip>
      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={onMenuClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        {menuItems}
      </Menu>

      {/* Mindset button */}
      <Tooltip title="Mindset" placement="top">
        <Fab
          color="purple"
          size="medium"
          className="footer__icon-button animate__animated animate__heartBeat animate__delay-2s"
          onClick={onMindsetClick}
        >
          <MoodIcon color="white" />
        </Fab>
      </Tooltip>

      {/* Add/Create button */}
      <Tooltip title={getAddButtonTooltip()} placement="top">
        <Fab
          color="green"
          size="medium"
          className="footer__icon-button animate__animated animate__heartBeat animate__delay-2s"
          onClick={onAddButtonClick}
        >
          <AddIcon fill="white" width={32} height={32} />
        </Fab>
      </Tooltip>

      {/* Preference dialog — shown on first create-task click */}
      <TaskCreationPreferenceDialog
        open={showPreferenceDialog}
        onClose={() => setShowPreferenceDialog(false)}
        onSelected={handlePreferenceSelected}
      />

      {/* Footer's own TaskHelper — only used when page doesn't provide its own */}
      {!externalOpenTaskHelper && (
        <TaskHelperDialog
          open={footerTaskHelperOpen}
          onClose={() => setFooterTaskHelperOpen(false)}
        />
      )}
    </div>
  );
};

export default AppFooter;
